
import "./main.css";
import React, { useMemo, useState, useEffect } from "react";
import { Button, message, Tag, Tooltip } from "antd";
import { CopyOutlined, ClockCircleFilled, CloseOutlined, CheckCircleFilled, FireFilled } from '@ant-design/icons';

function NewYearSaleBanner({ enableDismiss = false, onBuyNowClick }) {
    const [isVisible, setIsVisible] = useState(true);
    const [isIndia, setIsIndia] = useState(false);

    useEffect(() => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') {
            setIsIndia(true);
        }
    }, []);

    useEffect(() => {
        if (enableDismiss) {
            const hiddenTimestamp = localStorage.getItem("hideNewYearSale2026");
            if (hiddenTimestamp) {
                const hiddenTime = parseInt(hiddenTimestamp, 10);
                if (!isNaN(hiddenTime)) {
                    const hoursSince = (Date.now() - hiddenTime) / (1000 * 60 * 60);
                    if (hoursSince < 36) {
                        setIsVisible(false);
                    }
                } else if (hiddenTimestamp === "true") {
                    setIsVisible(false);
                }
            }
        }
    }, [enableDismiss]);

    // Generate snowflakes with random properties
    const snowflakes = useMemo(() => {
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            left: `${Math.random() * 100}%`,
            animationDuration: `${Math.random() * 3 + 2}s`,
            animationDelay: `${Math.random() * 5}s`,
            size: `${Math.random() * 4 + 2}px`,
            opacity: Math.random() * 0.5 + 0.3
        }));
    }, []);

    // Generate decorative text scribbles
    const scribbles = [
        { text: "comment PP", top: "10%", left: "5%", rotate: "-15deg" },
        { text: "Price", top: "20%", left: "85%", rotate: "10deg" },
        { text: "Link", top: "70%", left: "8%", rotate: "-5deg" },
        { text: "Guide", top: "80%", left: "75%", rotate: "15deg" },
        { text: "PDF", top: "15%", left: "15%", rotate: "5deg" },
        { text: "Website", top: "60%", left: "90%", rotate: "-10deg" },
        { text: "Buy", top: "30%", left: "80%", rotate: "20deg" },
        { text: "Follow me for link", top: "85%", left: "20%", rotate: "-8deg" },
        { text: "Sechdule with link", top: "10%", left: "70%", rotate: "5deg" },
        { text: "DM me this reel", top: "40%", left: "92%", rotate: "12deg" },
        { text: "Check DM", top: "50%", left: "2%", rotate: "-20deg" },
        { text: "Viral", top: "5%", left: "40%", rotate: "-5deg" },
        { text: "Trending", top: "90%", left: "40%", rotate: "3deg" },
        { text: "Auto Reply", top: "35%", left: "10%", rotate: "15deg" },
        { text: "Sales", top: "55%", left: "85%", rotate: "-12deg" },
        { text: "Growth", top: "25%", left: "92%", rotate: "8deg" },
        { text: "Automation", top: "75%", left: "90%", rotate: "-5deg" }
    ];

    // Live countdown timer state
    const [countdown, setCountdown] = useState("");

    // Calculate and update countdown every second
    useEffect(() => {
        const saleEndDate = new Date('2026-01-14T23:59:59');

        const updateCountdown = () => {
            const now = new Date();
            const diffTime = saleEndDate - now;

            if (diffTime <= 0) {
                setCountdown("Sale Ended");
                return;
            }

            const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diffTime % (1000 * 60)) / 1000);

            const pad = (n) => n.toString().padStart(2, '0');

            if (days > 0) {
                setCountdown(`${days} day${days > 1 ? 's' : ''} ${pad(hours)} hrs ${pad(minutes)} min ${pad(seconds)} sec`);
            } else {
                setCountdown(`${pad(hours)} hrs ${pad(minutes)} min ${pad(seconds)} sec`);
            }
        };

        updateCountdown(); // Initial call
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, []);

    if (!isVisible) return null;

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                padding: "30px 20px",
                marginBottom: "30px",
                color: "white",
                textAlign: "center",
                boxShadow: "0 10px 25px rgba(118, 75, 162, 0.3)",
                position: "relative",
                overflow: "hidden",
                marginTop: "20px"
            }}
        >
            {/* Background Scribbles (Desktop Only) */}
            <div className="desktop-scribbles" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}>
                {scribbles.map((item, index) => (
                    <div key={index} style={{
                        position: "absolute",
                        top: item.top,
                        left: item.left,
                        transform: `rotate(${item.rotate})`,
                        zIndex: 0
                    }}>
                        <div
                            className="floating-scribble"
                            style={{
                                fontFamily: "'Homemade Apple', cursive",
                                color: "rgba(255, 255, 255, 0.25)",
                                fontSize: "16px",
                                whiteSpace: "nowrap",
                                animationDelay: `${index * 0.2}s` // Staggered delay
                            }}
                        >
                            {item.text}
                        </div>
                    </div>
                ))}
            </div>
            {enableDismiss && (
                <div
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 100,
                        cursor: 'pointer',
                        padding: '5px',
                        background: 'rgba(0,0,0,0.2)',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.3s'
                    }}
                    onClick={() => {
                        localStorage.setItem("hideNewYearSale2026", Date.now().toString());
                        setIsVisible(false);
                    }}
                    className="banner-close-btn"
                >
                    <Tooltip title="Start saving now or miss the sale">
                        <CloseOutlined style={{ color: 'white', fontSize: '16px' }} />
                    </Tooltip>
                </div>
            )}

            {/* Noise Overlay */}
            <div className="noise-overlay"></div>

            {/* Snow Effect Container */}
            <div className="snow-container">
                {snowflakes.map((flake) => (
                    <div
                        key={flake.id}
                        className="snowflake"
                        style={{
                            left: flake.left,
                            width: flake.size,
                            height: flake.size,
                            animationDuration: flake.animationDuration,
                            animationDelay: flake.animationDelay,
                            opacity: flake.opacity
                        }}
                    />
                ))}
            </div>

            {/* Background Decor */}
            <div style={{
                position: "absolute",
                top: "-50px",
                right: "-50px",
                width: "150px",
                height: "150px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%"
            }}></div>
            <div style={{
                position: "absolute",
                bottom: "-30px",
                left: "-30px",
                width: "100px",
                height: "100px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%"
            }}></div>

            <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ marginBottom: "12px" }}>
                    <Tag
                        color="#ff4d4f"
                        style={{
                            border: "none",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                        }}
                        className="urgency-badge"
                    >
                        <ClockCircleFilled style={{ marginRight: "5px" }} />{countdown}
                    </Tag>
                </div>

                <h2 style={{ margin: "0 0 8px 0", fontSize: "28px", fontWeight: "800", letterSpacing: "-0.5px" }}>
                    <span className="text-shine">🎉 New Year Sale is Ending Soon.</span>
                </h2>

                <div style={{ color: "rgba(255,255,255,0.95)", fontSize: "16px", margin: "0 0 24px 0", fontWeight: "500", display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "monospace" }}>
                    <span className="typing-line-1">
                        Pay 8 Months & Get <span style={{ fontWeight: "bold", color: "#ffd700" }}>1 YEAR ACCESS</span>.
                    </span>
                    <span className="typing-line-2">
                        Don't miss out on this limited time offer!
                    </span>

                </div>

                <div style={{ position: "relative", display: "inline-block", marginTop: "20px" }}>
                    <div className="scribble-anim" style={{
                        position: "absolute",
                        top: "-45px",
                        right: "10px",
                        fontFamily: "'Homemade Apple', cursive",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        zIndex: 10
                    }}>
                        Pay 8 month get 1 year
                        <svg width="50" height="40" viewBox="0 0 50 40" style={{ position: "absolute", top: "20px", left: "80px", zIndex: -1, pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg">
                            <path d="M30,0 Q10,20 10,35" stroke="white" strokeWidth="2" fill="none" opacity="0.9" strokeLinecap="round" />
                            <path d="M5,30 L10,35 L15,30" stroke="white" strokeWidth="2" fill="none" opacity="0.9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "12px",
                        background: "rgba(0,0,0,0.25)",
                        padding: "8px 8px 8px 20px",
                        borderRadius: "100px",
                        border: "1px solid rgba(255,255,255,0.3)",
                        backdropFilter: "blur(4px)"
                    }}>
                        <span style={{ fontSize: "15px", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                            {isIndia ? "Coupon Applied:" : "Discount Code:"}
                        </span>
                        <Button
                            type="primary"
                            shape="round"
                            icon={isIndia ? <CheckCircleFilled /> : <CopyOutlined />}
                            size="large"
                            className="discount-green-btn"
                            onClick={() => {
                                if (isIndia) {
                                    message.success("Coupon already applied for your region!");
                                    return;
                                }
                                navigator.clipboard.writeText("2026");
                                message.success("Discount code '2026' copied to clipboard!");
                            }}
                        >
                            2026
                        </Button>
                    </div>
                </div>

                {onBuyNowClick && (
                    <div style={{ marginTop: "25px" }}>
                        <Button
                            type="primary"
                            size="large"
                            shape="round"
                            style={{
                                background: "#ffd700",
                                borderColor: "#ffd700",
                                color: "#000000ff",
                                fontWeight: "bold",
                                padding: "0 40px",
                                height: "45px",
                                fontSize: "16px",
                                boxShadow: "0 4px 15px rgba(255, 215, 0, 0.4)"
                            }}
                            onClick={onBuyNowClick}
                        >
                            Buy Now
                        </Button>
                    </div>
                )}
                <div style={{ marginTop: "20px", color: "#ffec3d", fontWeight: "bold", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px", textShadow: "0 2px 10px rgba(255, 100, 0, 0.4)", justifyContent: "center" }}>
                    <FireFilled style={{ fontSize: "16px", animation: "pulse 1.5s infinite" }} />Only {Math.max(3, 20 - Math.floor((Date.now() - new Date("2025-12-25").getTime()) / (86400000)))} Spots Left for this price!
                </div>
            </div>
        </div>
    )
}

export default NewYearSaleBanner;
